'use client'

import { useSyncExternalStore } from 'react'

interface ScrollState {
  isVisible: boolean
  scrollY: number
  direction: 'up' | 'down'
}

const createScrollStore = (thresholdDown = 50, thresholdUp = 5) => {
  let state: ScrollState = {
    isVisible: true,
    scrollY: 0,
    direction: 'up',
  }

  let lastScrollY = 0
  let ticking = false
  const listeners = new Set<() => void>()

  const updateScrollState = () => {
    const scrollY = window.pageYOffset
    const direction = scrollY > lastScrollY ? 'down' : 'up'
    const scrollDelta = Math.abs(scrollY - lastScrollY)

    // Use different thresholds for up vs down scrolling
    const threshold = direction === 'up' ? thresholdUp : thresholdDown

    if (scrollDelta > threshold) {
      const isVisible = direction === 'up' || scrollY < thresholdDown

      state = {
        isVisible,
        scrollY,
        direction,
      }

      // Notify all subscribers
      for (const listener of listeners) {
        listener()
      }
    }

    lastScrollY = scrollY > 0 ? scrollY : 0
    ticking = false
  }

  const onScroll = () => {
    if (!ticking) {
      requestAnimationFrame(updateScrollState)
      ticking = true
    }
  }

  const subscribe = (listener: () => void) => {
    listeners.add(listener)

    // Add scroll listener on first subscription
    if (listeners.size === 1) {
      window.addEventListener('scroll', onScroll, { passive: true })
    }

    return () => {
      listeners.delete(listener)

      // Remove scroll listener when no more subscriptions
      if (listeners.size === 0) {
        window.removeEventListener('scroll', onScroll)
      }
    }
  }

  const getSnapshot = () => state

  // Cache the server snapshot to avoid infinite loops
  const serverSnapshot: ScrollState = {
    isVisible: true,
    scrollY: 0,
    direction: 'up',
  }
  const getServerSnapshot = () => serverSnapshot

  return { subscribe, getSnapshot, getServerSnapshot }
}

// Create stores for common threshold combinations
const stores = new Map<string, ReturnType<typeof createScrollStore>>()

const getStore = (thresholdDown: number, thresholdUp: number) => {
  const key = `${thresholdDown}-${thresholdUp}`
  if (!stores.has(key)) {
    stores.set(key, createScrollStore(thresholdDown, thresholdUp))
  }
  return stores.get(key)!
}

export const useScrollDirection = (thresholdDown = 50, thresholdUp = 5) => {
  const store = getStore(thresholdDown, thresholdUp)

  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot,
  )
}
